import React, { useContext, useEffect, useState } from "react";
import { useAccess } from "../../../access/hooks/useAccess";
import { Context } from "../../../context/Context";
import { readCashierSelection, saveCashierSelection } from "../../../utils/cashierSelection";
import { notifyError } from "../../../utils/notify";
import { serializeHours } from "../hooks/useDashboard";
import { hoursErrorMessage } from "../../../utils/workingHoursErrors";
import { revealCredentialAPI } from "../../../utils/api/ludDashboard";
import { Eye, EyeOff } from "../../ui/icons";
import useStoreLogo from "../hooks/useStoreLogo";
import { ludDashboardPermissions } from "../../../access/modules/lud-dashboard";
import { Button, Card, Select, Tags } from "../../ui";
import WorkingHoursModal from "../../modals/WorkingHoursModal";
import MaintenanceClosureModal from "../../modals/MaintenanceClosureModal";
import {
  integrationLabel,
  normalizeIntegrationKey,
} from "../../../utils/integrations";
import Requests from "../Requests/Requests";
import { DASHBOARD_TEXT } from "../data/dashboardText";
import { describeSubscriptionCard } from "../data/subscriptionSummary";

// Canonical integration key; unknown names compare case/space-insensitively.
const appKeyOf = (value) =>
  normalizeIntegrationKey(value) ||
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const NO_BRANCHES = [];

// Store logo, or the store's initial when it has none (or the image fails).
function CashierLogo({ name, url = "", onError }) {
  return (
    <div className={`lud-cashier-logo${url ? " has-image" : ""}`}>
      {url ? (
        <img src={url} alt={name} onError={onError} />
      ) : (
        <span aria-hidden="true">{String(name || "").trim().charAt(0) || "•"}</span>
      )}
    </div>
  );
}

export default function CashierView({ lang, dashboard }) {
  const { can, has } = useAccess();
  const context = useContext(Context);
  // Only approved branch users may look at passwords (the server enforces it:
  // POST /lud-dashboard/credentials/:storeId/reveal). Masked unless toggled.
  const canRevealPasswords = has(ludDashboardPermissions.credentials.reveal) && context?.currentUserInfo?.approved === true;
  const [revealed, setRevealed] = useState({});
  const [revealing, setRevealing] = useState("");
  const [revealError, setRevealError] = useState("");
  const userKey = context?.currentUserInfo?._id || context?.currentUserInfo?.id;
  const [branchId, setBranchId] = useState("");
  const [hoursModalOpen, setHoursModalOpen] = useState(false);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);

  const storeId = dashboard.selectedStoreId;
  // The overview in state belongs to the selected store only once it has
  // loaded; until then nothing of the previous store is shown.
  const storeReady = Boolean(storeId) && dashboard.store?.id === storeId;
  const branches = storeReady ? dashboard.branches : NO_BRANCHES;
  const summaryOf = (id) => (dashboard.stores || []).find((item) => item.id === id);
  const storeNameOf = (item) =>
    item ? (lang === "ar" ? item.nameAr : item.nameEn) : "";
  const { url: logoUrl } = useStoreLogo(storeId);
  const [logoFailed, setLogoFailed] = useState("");

  // Keep the chosen branch valid for the selected store: the remembered one if
  // it is still reachable, otherwise the first accessible branch.
  useEffect(() => {
    if (!storeReady) {
      setBranchId("");
      return;
    }
    if (branches.some((item) => item.id === branchId)) return;
    const remembered = readCashierSelection(userKey).branches[storeId];
    const next = branches.find((item) => item.id === remembered) || branches[0];
    setBranchId(next?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeReady, storeId, branches, branchId]);

  useEffect(() => {
    if (storeReady && branchId && branches.some((item) => item.id === branchId))
      saveCashierSelection(userKey, { storeId, branchId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeReady, storeId, branchId]);

  const branch = branches.find((item) => item.id === branchId);

  // Revealed passwords never outlive the store/branch they were shown for.
  useEffect(() => {
    setRevealed({});
    setRevealError("");
  }, [storeId, branchId]);

  const toggleReveal = async (account) => {
    const key = `${account.scope}:${account.id}`;
    if (revealed[key] !== undefined) {
      setRevealed(({ [key]: _hidden, ...rest }) => rest);
      return;
    }
    if (revealing) return;
    setRevealing(key);
    setRevealError("");
    try {
      const body = account.scope === "store" ? { accountId: account.id } : { branchId: account.branchId, loginId: account.id };
      const res = await revealCredentialAPI(localStorage.getItem("auth-token"), storeId, body);
      setRevealed((current) => ({ ...current, [key]: res.password || "" }));
    } catch (error) {
      setRevealError(error?.message || (lang === "ar" ? "تعذر عرض كلمة المرور" : "The password could not be shown"));
    } finally {
      setRevealing("");
    }
  };

  const branchApplications = (branch?.appLinks || [])
    .filter((item) => item.status === "active")
    .map((item, index) => ({
      id: String(item.id || `${item.app}-${index}`),
      key: appKeyOf(item.app || item.name),
      nameAr: integrationLabel(item.app || item.name, "ar"),
      nameEn: integrationLabel(item.app || item.name, "en"),
      status: item.status || "inactive",
    }));

  const branchApplicationKeys = new Set(
    branchApplications.map((item) => item.key),
  );
  const branchUserAccounts = [
    ...(branch?.users || []).map((user) => ({
      id: user.id,
      app: user.app,
      email: user.email,
      hasSecret: user.hasSecret,
      scope: "branch",
      branchId: branch?.id,
      status: user.status || "active",
    })),
    ...dashboard.accounts
      .filter((account) => branchApplicationKeys.has(appKeyOf(account.app)))
      .map((account) => ({ ...account, scope: "store" })),
  ].filter(
    (account, index, rows) =>
      rows.findIndex(
        (item) =>
          appKeyOf(item.app) === appKeyOf(account.app) &&
          String(item.email || "")
            .trim()
            .toLowerCase() ===
            String(account.email || "")
              .trim()
              .toLowerCase(),
      ) === index,
  );

  const branchRequests = dashboard.requests.filter(
    (request) =>
      request.target === branch?.nameAr ||
      request.payload?.branchId === branch?.id,
  );

  const pendingHoursRequest = branchRequests.find(
    (request) =>
      request.type === "تعديل أوقات العمل" &&
      !["approved", "completed", "rejected", "canceled"].includes(request.status),
  );

  // The server validates the request (assigned branch, hours, one open request
  // per branch); its reason is shown instead of failing silently.
  const reportRequestError = (error) =>
    notifyError({
      en: hoursErrorMessage(error, "en") || "The request could not be sent",
      ar: `تعذّر إرسال الطلب: ${hoursErrorMessage(error, "ar")}`,
    });

  const submitHoursRequest = async (hours) => {
    try {
      await dashboard.createRequest({
        createdBy: "cashier",
        type: "تعديل أوقات العمل",
        target: branch.nameAr,
        details: "تعديل أوقات العمل اليومية",
        payload: {
          action: "updateBranchHours",
          branchId: branch.id,
          workTime: [serializeHours(hours)],
        },
      });
      setHoursModalOpen(false);
    } catch (error) {
      reportRequestError(error);
    }
  };

  const submitMaintenanceRequest = async ({ from, to, reason }) => {
    try {
      await dashboard.createRequest({
        createdBy: "cashier",
        type: "إغلاق صيانة",
        target: branch.nameAr,
        details: `طلب إغلاق للصيانة — من ${from} إلى ${to}`,
        payload: {
          action: "maintenanceClosure",
          branchId: branch.id,
          from,
          to,
          ...(reason ? { reason } : {}),
        },
      });
      setMaintenanceModalOpen(false);
    } catch (error) {
      reportRequestError(error);
    }
  };

  const storeName =
    lang === "ar"
      ? dashboard.store?.businessName || dashboard.store?.name || "—"
      : dashboard.store?.moreDetails?.businessNameEn ||
        dashboard.store?.businessName ||
        dashboard.store?.name ||
        "—";

  const selectors = (
    <div className="lud-cashier-selectors">
      {(dashboard.stores || []).length > 1 && (
        <Select
          value={storeId}
          onChange={(event) => dashboard.setSelectedStoreId(event.target.value)}
          options={(dashboard.stores || []).map((item) => ({
            value: item.id,
            label: storeNameOf(item),
          }))}
        />
      )}
      <Select
        value={branchId}
        disabled={!storeReady || branches.length === 0}
        placeholder={lang === "ar" ? "اختر الفرع" : "Select branch"}
        onChange={(event) => setBranchId(event.target.value)}
        options={branches.map((item) => ({
          value: item.id,
          label: lang === "ar" ? item.nameAr : item.nameEn,
        }))}
      />
    </div>
  );

  // Switching stores (or a store that failed to load) shows a clean state
  // instead of the previous store's data.
  if (!storeReady) {
    const pendingName = storeNameOf(summaryOf(storeId));
    return (
      <div
        className="lud-dashboard-subpage lud-cashier-page"
        dir={lang === "ar" ? "rtl" : "ltr"}
      >
        <div className="lud-dashboard-subpage-head">
          <h1>{lang === "ar" ? "صفحة الكاشير" : "Cashier Page"}</h1>
        </div>
        <section className="lud-cashier-hero">
          <div className="lud-cashier-brand">
            <CashierLogo name={pendingName} url={logoUrl} />
            <div>
              <h2>{pendingName || "—"}</h2>
            </div>
          </div>
          {selectors}
        </section>
        <p className="lud-muted" role="status">
          {dashboard.error ||
            (lang === "ar" ? "جاري التحميل..." : "Loading...")}
        </p>
      </div>
    );
  }

  return (
    <div
      className="lud-dashboard-subpage lud-cashier-page"
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <div className="lud-dashboard-subpage-head">
        <h1>{lang === "ar" ? "صفحة الكاشير" : "Cashier Page"}</h1>

        {can(ludDashboardPermissions.backToDashboard) && (
          <Button onClick={() => dashboard.setPage("dashboard")}>
            {lang === "ar" ? "رجوع للوحة الرئيسية" : "Back"}
          </Button>
        )}
      </div>

      <section className="lud-cashier-hero">
        <div className="lud-cashier-brand">
          <CashierLogo
            name={storeName}
            url={logoFailed === logoUrl ? "" : logoUrl}
            onError={() => setLogoFailed(logoUrl)}
          />

          <div>
            <h2>{storeName}</h2>

            <p>
              {branch?.nameAr} ·{" "}
              {dashboard.selectedStoreId || dashboard.store?.id || ""} ·{" "}
              {branch?.phone}
            </p>
          </div>
        </div>

        {selectors}
      </section>

      <section
        className={`lud-cashier-banner${dashboard.banner?.url ? " has-image" : ""}`}
      >
        {dashboard.banner?.url ? (
          <img src={dashboard.banner.url} alt="" />
        ) : (
          <strong>
            {lang === "ar"
              ? "مساحة إعلانية للتطبيقات (بنر) — 1200×250"
              : "Application Advertising Space — 1200×250"}
          </strong>
        )}
      </section>

      <section className="lud-cashier-subscription-grid">
        {[
          ["platform", DASHBOARD_TEXT[lang].subscription.platformCardTitle],
          [
            "integration",
            DASHBOARD_TEXT[lang].subscription.integrationCardTitle,
          ],
        ].map(([kind, title]) => {
          const card = describeSubscriptionCard(
            dashboard.selectedSubscription,
            kind,
            DASHBOARD_TEXT[lang],
            lang,
          );
          return (
            <Card
              key={kind}
              className={`lud-cashier-subscription-card${card.warning ? " is-warning" : ""}`}
            >
              <h2>{title}</h2>
              <strong>{card.days}</strong>
              <span>{card.caption}</span>
              {card.note && <small>{card.note}</small>}
              {card.status && <small>{card.status}</small>}
            </Card>
          );
        })}
      </section>

      <section className="lud-dashboard-card lud-cashier-contact-card">
        <div className="lud-dashboard-section-title">
          <h2>
            {lang === "ar" ? "بيانات التواصل والموقع" : "Contact and Location"}
          </h2>
        </div>

        <div className="lud-cashier-contact-list">
          <div>
            <span>{lang === "ar" ? "رقم التواصل" : "Contact Number"}</span>
            <strong>{branch?.phone || "—"}</strong>
          </div>

          <div>
            <span>{lang === "ar" ? "المدينة" : "City"}</span>
            <strong>{lang === "ar" ? branch?.cityAr : branch?.cityEn}</strong>
          </div>

          <div>
            <span>{lang === "ar" ? "رابط الموقع" : "Location Link"}</span>

            {branch?.locationUrl ? (
              <a href={branch.locationUrl} target="_blank" rel="noreferrer">
                {lang === "ar" ? "فتح الموقع" : "Open Location"}
              </a>
            ) : (
              <strong>—</strong>
            )}
          </div>
        </div>
      </section>

      <section className="lud-dashboard-card">
        <div className="lud-dashboard-section-title">
          <h2>
            {lang === "ar"
              ? "التطبيقات الفعالة عندك"
              : "Your Active Applications"}
          </h2>
        </div>

        <div className="lud-cashier-apps">
          {branchApplications.map((item) => (
            <Card key={item.id}>
              <strong>{lang === "ar" ? item.nameAr : item.nameEn}</strong>

              <Tags
                variant={
                  item.status === "active"
                    ? "success"
                    : item.status === "inProgress"
                      ? "warning"
                      : "danger"
                }
                appearance="soft"
                size="sm"
              >
                {item.status === "active"
                  ? lang === "ar"
                    ? "فعال"
                    : "Active"
                  : item.status === "inProgress"
                    ? lang === "ar"
                      ? "جاري العمل"
                      : "In Progress"
                    : lang === "ar"
                      ? "غير فعال"
                      : "Inactive"}
              </Tags>
            </Card>
          ))}
        </div>
      </section>

      <section className="lud-dashboard-card lud-cashier-hours-card">
        <div className="lud-dashboard-section-title">
          <div>
            <h2>{lang === "ar" ? "أوقات العمل" : "Working Hours"}</h2>

            <p className="lud-muted">
              {lang === "ar"
                ? "يراجع المطعم أو إدارة LUD الطلب ثم يُبلَّغ كل تطبيق فعّال، وتتحدث الأوقات بعد أن تؤكد التطبيقات ويُنهى الطلب. تابع مرحلة الطلب أدناه."
                : "The restaurant or LUD reviews the request, every active application is notified, and the hours update after the applications confirm and the request is finalized. Follow its stage below."}
            </p>
          </div>

          <div className="lud-cashier-hours-actions">
            <Button
              variant="primary"
              appearance="filled"
              onClick={() => setHoursModalOpen(true)}
            >
              {lang === "ar" ? "طلب تعديل الأوقات" : "Request Hours Change"}
            </Button>

            <Button
              variant="danger"
              appearance="soft"
              onClick={() => setMaintenanceModalOpen(true)}
            >
              {lang === "ar"
                ? "طلب إغلاق صيانة"
                : "Maintenance Closure Request"}
            </Button>
          </div>
        </div>

        {pendingHoursRequest && (
          <div className="lud-cashier-pending-note">
            <Tags variant="warning" appearance="soft" size="sm">
              {lang === "ar" ? "جاري العمل" : "In Progress"}
            </Tags>

            <span>
              {lang === "ar"
                ? "طلب تعديل الأوقات قيد المتابعة. تفاصيل مرحلته أدناه."
                : "The hours-change request is in progress. Its stage is shown below."}
            </span>
          </div>
        )}

        <div className="lud-table-scroll">
          <table className="lud-dashboard-table">
            <thead>
              <tr>
                <th>{lang === "ar" ? "اليوم" : "Day"}</th>
                <th>{lang === "ar" ? "الفترة الأولى" : "First Shift"}</th>
                <th>{lang === "ar" ? "الفترة الثانية" : "Second Shift"}</th>
                <th>{lang === "ar" ? "الحالة" : "Status"}</th>
              </tr>
            </thead>

            <tbody>
              {(branch?.hours || []).map((day) => (
                <tr key={day.dayEn}>
                  <td>{lang === "ar" ? day.dayAr : day.dayEn}</td>

                  <td>{day.closed ? "—" : `${day.from1} - ${day.to1}`}</td>

                  <td>
                    {day.closed || !day.from2
                      ? "—"
                      : `${day.from2} - ${day.to2}`}
                  </td>

                  <td>
                    {day.closed
                      ? lang === "ar"
                        ? "مغلق"
                        : "Closed"
                      : lang === "ar"
                        ? "فعال"
                        : "Open"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="lud-dashboard-card">
        <div className="lud-dashboard-section-title">
          <h2>
            {lang === "ar"
              ? "حسابات المستخدم في الفرع"
              : "Branch User Accounts"}
          </h2>
        </div>

        <div className="lud-table-scroll">
          <table className="lud-dashboard-table">
            <thead>
              <tr>
                <th>{lang === "ar" ? "التطبيق" : "App"}</th>
                <th>{lang === "ar" ? "الإيميل" : "Email"}</th>
                <th>{lang === "ar" ? "الرقم السري" : "Password"}</th>
                <th>{lang === "ar" ? "الحالة" : "Status"}</th>
              </tr>
            </thead>

            <tbody>
              {branchUserAccounts.map((account) => (
                <tr key={account.id}>
                  <td>{integrationLabel(account.app, lang)}</td>
                  <td>{account.email || "—"}</td>
                  <td>
                    {(() => {
                      const key = `${account.scope}:${account.id}`;
                      const shown = revealed[key];
                      if (!account.hasSecret) return "—";
                      return (
                        <span className="lud-secret-cell">
                          <span dir="ltr">{shown !== undefined ? shown || "—" : "••••••••"}</span>
                          {canRevealPasswords && (
                            <button
                              type="button"
                              className="lud-secret-toggle"
                              onClick={() => toggleReveal(account)}
                              disabled={revealing === key}
                              aria-pressed={shown !== undefined}
                              aria-label={
                                shown !== undefined
                                  ? lang === "ar" ? "إخفاء كلمة المرور" : "Hide password"
                                  : lang === "ar" ? "إظهار كلمة المرور" : "Show password"
                              }
                              title={
                                shown !== undefined
                                  ? lang === "ar" ? "إخفاء كلمة المرور" : "Hide password"
                                  : lang === "ar" ? "إظهار كلمة المرور" : "Show password"
                              }
                            >
                              {shown !== undefined ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {account.status === "active"
                      ? lang === "ar"
                        ? "فعال"
                        : "Active"
                      : account.status === "inProgress"
                        ? lang === "ar"
                          ? "جاري العمل"
                          : "In Progress"
                        : lang === "ar"
                          ? "غير فعال"
                          : "Inactive"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {revealError && (
            <p className="lud-hours-error" role="alert">
              {revealError}
            </p>
          )}
        </div>
      </section>

      <Requests
        lang={lang}
        text={DASHBOARD_TEXT[lang]}
        showShortcuts={false}
        selectedStoreId={storeId}
        branches={dashboard.branches}
        reloadKey={dashboard.requests}
        onChanged={dashboard.refresh}
        onCreateRequest={(request) =>
          dashboard.createRequest({
            ...request,
            createdBy: "cashier",
          })
        }
        onOpenRequestFile={dashboard.openRequestFile}
      />

      <WorkingHoursModal
        open={hoursModalOpen}
        lang={lang}
        text={DASHBOARD_TEXT[lang]}
        branch={branch}
        hours={branch?.hours || []}
        requestMode
        onClose={() => setHoursModalOpen(false)}
        onSubmit={submitHoursRequest}
      />

      <MaintenanceClosureModal
        open={maintenanceModalOpen}
        lang={lang}
        text={DASHBOARD_TEXT[lang]}
        branch={branch}
        branches={dashboard.branches}
        lockBranch
        onClose={() => setMaintenanceModalOpen(false)}
        onSubmit={submitMaintenanceRequest}
      />
    </div>
  );
}
